type ScopeGroup = {
    key: string;
    label: string;
    scopes: string[];
};

type UserGuideScopeOption = {
    label: string;
    value: string;
    secondaryLabel?: string;
};

type UserGuideScopeConfig = {
    scope: string;
    name: string;
};

export type UserGuideScopeSelectionOption = {
    label: string;
    value: string;
    scopes: string[];
    groupLabel?: string;
    secondaryLabel?: string;
    disabled?: boolean;
};

const scopeGroups: ScopeGroup[] = [
    {
        key: 'insights',
        label: 'Insights',
        scopes: [
            'insights:content-edit',
            'insights:content-publish',
            'insights:content-reader',
            'insights:content-reader-trial',
        ],
    },
];

const toUniqueSorted = (scopes: string[]): string[] =>
    Array.from(
        new Set(scopes.map((scope) => scope.trim()).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));

const dashboardBuiltInAccessScopes = new Set(['csc', 'fpt']);
const dashboardHiddenPermissionPrefixes = [
    'admin:',
    'connect:',
    'external:',
    'msanalytics:',
    'research:',
    'xtp:',
];

const getDashboardSelectableScopes = (scopes: string[]): string[] => {
    const normalizedScopes = toUniqueSorted(scopes);
    const scopeSet = new Set(normalizedScopes);

    return normalizedScopes.filter((scope) => {
        if (scope.includes(':')) {
            return !dashboardHiddenPermissionPrefixes.some((prefix) =>
                scope.startsWith(prefix)
            );
        }

        return (
            dashboardBuiltInAccessScopes.has(scope) ||
            scopeSet.has(`admin:${scope}`)
        );
    });
};

const isAdminLikeScope = (scope: string): boolean => {
    const normalizedScope = scope.trim().toLowerCase();

    return (
        normalizedScope.startsWith('admin:') ||
        normalizedScope.endsWith(':admin') ||
        normalizedScope.endsWith('-admin')
    );
};

const toDisplayLabel = (value: string): string =>
    value
        .split(/[-_]/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');

export const getUserGuideScopeSelectionOptions = (
    availableNeonScopes: string[] | undefined | null,
    scopeConfigs: Array<{ scope?: string; name?: string }> | undefined | null,
    currentScopes?: string[]
): UserGuideScopeSelectionOption[] => {
    const normalizedCurrentScopes = toUniqueSorted(currentScopes || []);
    const currentScopeSet = new Set(normalizedCurrentScopes);
    const assignedScopeNames = new Map<string, string>();
    (scopeConfigs || []).forEach((config) => {
        const scope = (config.scope || '').trim();

        if (scope && !currentScopeSet.has(scope)) {
            assignedScopeNames.set(scope, (config.name || '').trim());
        }
    });

    const getAssignmentLabel = (scopes: string[]): string | undefined => {
        const assignedNames = toUniqueSorted(
            scopes
                .map((scope) => assignedScopeNames.get(scope) || '')
                .filter(Boolean)
        );

        if (!scopes.some((scope) => assignedScopeNames.has(scope))) {
            return undefined;
        }

        return assignedNames.length
            ? `Already assigned to ${assignedNames.join(', ')}`
            : 'Already assigned to another user guide group';
    };

    const visibleCatalogueScopes = toUniqueSorted(
        availableNeonScopes || []
    ).filter(
        (scope) =>
            scope !== 'admin' &&
            !isAdminLikeScope(scope) &&
            !dashboardHiddenPermissionPrefixes.some((prefix) =>
                scope.startsWith(prefix)
            )
    );
    const options: UserGuideScopeSelectionOption[] = visibleCatalogueScopes
        .filter((scope) => !scope.includes(':'))
        .map((scope) => {
            const secondaryLabel = getAssignmentLabel([scope]);

            return {
                label: toDisplayLabel(scope),
                value: scope,
                scopes: [scope],
                secondaryLabel,
                disabled: !!secondaryLabel,
            };
        });
    visibleCatalogueScopes
        .filter((scope) => scope.includes(':'))
        .forEach((scope) => {
            const application = scope.slice(0, scope.indexOf(':'));
            const permission = scope.slice(scope.indexOf(':') + 1);
            const secondaryLabel = getAssignmentLabel([scope]);

            options.push({
                label: toDisplayLabel(permission),
                value: scope,
                scopes: [scope],
                groupLabel: `${toDisplayLabel(application)} Permissions`,
                secondaryLabel,
                disabled: !!secondaryLabel,
            });
        });

    const representedScopes = new Set(
        options.flatMap((option) => option.scopes)
    );
    normalizedCurrentScopes
        .filter((scope) => !representedScopes.has(scope))
        .forEach((scope) => {
            options.push({
                label: toDisplayLabel(scope),
                value: scope,
                scopes: [scope],
            });
        });

    return options.sort((first, second) => {
        const groupComparison = (first.groupLabel || '').localeCompare(
            second.groupLabel || ''
        );

        return groupComparison || first.label.localeCompare(second.label);
    });
};

const groupedScopeSet = new Set(scopeGroups.flatMap((group) => group.scopes));

export const getAvailableUserGuideScopeOptions = (
    availableNeonScopes: string[] | undefined | null,
    scopeConfigs: UserGuideScopeConfig[] | undefined | null
): UserGuideScopeOption[] => {
    const scopeConfigMap = new Map(
        (scopeConfigs || [])
            .map((config) => ({
                scope: (config.scope || '').trim(),
                name: (config.name || '').trim(),
            }))
            .filter((config) => !!config.scope)
            .map((config) => [config.scope, config.name])
    );

    const allScopes = toUniqueSorted([
        ...getDashboardSelectableScopes(availableNeonScopes || []),
        ...Array.from(scopeConfigMap.keys()),
    ]);

    const scopeOptions = allScopes
        .filter(
            (scope) => !isAdminLikeScope(scope) && !groupedScopeSet.has(scope)
        )
        .map((scope) => {
            const configuredName = scopeConfigMap.get(scope) || scope;

            return {
                label: configuredName,
                value: scope,
                secondaryLabel: configuredName !== scope ? scope : undefined,
            };
        });

    const groupOptions = scopeGroups.map((group) => ({
        label: group.label,
        value: group.key,
        secondaryLabel: group.scopes.sort().join(', '),
    }));

    return [...groupOptions, ...scopeOptions];
};

export const getAvailableUserGuideScopeValues = (
    availableNeonScopes: string[] | undefined | null,
    scopeConfigs: Array<{ scope?: string }> | undefined | null,
    currentScopes?: string | string[]
): string[] => {
    const normalizedCurrentScopes = (
        Array.isArray(currentScopes) ? currentScopes : [currentScopes]
    )
        .map((scope) => (scope || '').trim())
        .filter(Boolean);

    const assignedScopes = new Set(
        (scopeConfigs || [])
            .map((config) => (config.scope || '').trim())
            .filter(Boolean)
    );

    normalizedCurrentScopes.forEach((scope) => assignedScopes.delete(scope));

    const allScopes = toUniqueSorted([
        ...getDashboardSelectableScopes(availableNeonScopes || []),
        ...normalizedCurrentScopes,
    ]);

    return allScopes.filter(
        (scope) => !isAdminLikeScope(scope) && !assignedScopes.has(scope)
    );
};

export const mapStoredUserGuideScopesToSelectableValues = (
    scopes: string[] | undefined | null
): string[] => {
    if (!scopes || scopes.length === 0) {
        return [];
    }

    const remainingScopes = new Set(
        toUniqueSorted(scopes).filter((scope) => !isAdminLikeScope(scope))
    );
    const selectedValues: string[] = [];

    scopeGroups.forEach((group) => {
        const includesAnyScope = group.scopes.some((scope) =>
            remainingScopes.has(scope)
        );
        if (!includesAnyScope) {
            return;
        }

        selectedValues.push(group.key);
        group.scopes.forEach((scope) => remainingScopes.delete(scope));
    });

    selectedValues.push(...Array.from(remainingScopes));

    return toUniqueSorted(selectedValues);
};

export const expandSelectedUserGuideScopeValuesToStoredScopes = (
    values: string[] | undefined | null
): string[] => {
    if (!values || values.length === 0) {
        return [];
    }

    const scopeGroupMap = new Map(
        scopeGroups.map((group) => [group.key, group.scopes])
    );

    const expandedScopes = values.flatMap(
        (value) => scopeGroupMap.get(value) || [value]
    );

    return toUniqueSorted(
        expandedScopes.filter((scope) => !isAdminLikeScope(scope))
    );
};
